import { Button, Flex, Text, theme } from '../../../2022-component-library';
import { useTestContext } from '../../../context/testContext';
import { useAppointment } from '../../../context/appointmentContext';
import { client } from '../../../helpers/api-client';
import { useEffect, useState } from 'react';
import { formattedRefractionScore } from '../../../helpers/refraction';
import { confirmNavigation } from '../../../helpers/functions';
import { useResultData } from '../../../helpers/useResultData';

const ResponsiveRefractionResults = ({ updateStep, setActiveTab, goBackToCompletedTests }) => {
  const { testData } = useTestContext();
  const viewingLocalTestResults = testData.leftEye || testData.rightEye || testData.bothEyes;
  const [testResults, setTestResults] = useState({});
  const { appointment } = useAppointment();
  const { clearAllResults } = useResultData();

  const sendTestResults = () => {
    const { corrected } = testData;
    let testResultData = { leftEye: 'Eye not tested', rightEye: 'Eye not tested', corrected };
    if (testData.testRightEye && testData.rightEye) {
      testResultData['rightEye'] = testData.rightEye;
    }

    if (testData.testLeftEye && testData.leftEye) {
      testResultData['leftEye'] = testData.leftEye;
    }

    if (testData.testBothEyes && testData.bothEyes) {
      testResultData['bothEyes'] = testData.bothEyes;
    }

    const sendTestResultData = {
      appointment_eye_test: testData.eyeTestId,
      appointment: appointment.currentAppointment.id,
      result_data: JSON.stringify(testResultData),
    };

    if (window.confirm('Are you sure you want to save the test results?') === true) {
      client(`api/appointment-test-result/`, { body: sendTestResultData }).then(
        () => {
          setActiveTab('results');
        },
        (error) => {
          console.error('There was an error saving the results:', error);
        },
      );
    }
  };

  const ResultsTable = ({ eye }) => {
    const result = testResults[eye];
    const title = (eye.charAt(0).toUpperCase() + eye.slice(1)).match(/[A-Z][a-z]+/g).join(' ');
    const bgColor = eye === 'rightEye' ? theme.colors.primary : theme.colors.primaryTint;
    const contrastingTextColor = eye === 'rightEye' ? 'white' : 'black;';

    return (
      <Flex column width='40vw' maxWidth='350px' centered borderRight={eye === 'leftEye' ? '1px solid #000' : null}>
        <Flex width='100%' centered padding='1rem' backgroundColor={bgColor}>
          <Text size='h2' color={contrastingTextColor}>
            {title}
          </Text>
        </Flex>
        <Flex width='100%' centered padding='1rem'>
          <Text size='subHeading'>{result?.score ? `Sphere: ${formattedRefractionScore(result.score.sphere, 2)}` : 'N/A'}</Text>
        </Flex>
        <Flex width='100%' centered padding='1rem' backgroundColor={bgColor}>
          <Text size='subHeading' color={contrastingTextColor}>
            {result?.score ? `Cylinder: ${formattedRefractionScore(result.score.cylinder, 2)}` : 'N/A'}
          </Text>
        </Flex>
        <Flex width='100%' centered padding='1rem'>
          <Text size='subHeading'>{result?.score ? `Axis: ${formattedRefractionScore(result.score.axis, 0)}` : 'N/A'}</Text>
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    const getTestResultsFromServer = async () => {
      const testId = testData.testResultsId;
      client(`api/appointment-test-result/${testId}`).then(
        (data) => {
          const parsedResults = data.result_data ? JSON.parse(data.result_data) : [];
          setTestResults(parsedResults);
        },
        (error) => {
          console.error(error);
        },
      );
    };

    if (viewingLocalTestResults) {
      setTestResults(testData);
    } else {
      getTestResultsFromServer();
    }
  }, [viewingLocalTestResults, testData]);

  return (
    <Flex column centered>
      <Flex centered>
        {testResults.bothEyes ? (
          <ResultsTable eye='bothEyes' />
        ) : (
          <>
            <ResultsTable eye='rightEye' />
            <ResultsTable eye='leftEye' />
          </>
        )}
      </Flex>
      <Flex marginTop='2rem' width='50vw' justifyContent={goBackToCompletedTests ? 'center' : 'space-between'}>
        <Button
          variant='outlined'
          width='calc(25vw - 2rem)'
          onClick={
            goBackToCompletedTests
              ? goBackToCompletedTests
              : () =>
                  confirmNavigation('restart').then(() => {
                    clearAllResults();
                    return updateStep('eyeSetup');
                  })
          }
        >
          {goBackToCompletedTests ? 'Back' : 'Restart Test'}
        </Button>
        {!goBackToCompletedTests && (
          <Button width='calc(25vw - 2rem)' onClick={() => sendTestResults()}>
            Save Test Results
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default ResponsiveRefractionResults;
